import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadCommonHeaders } from "../../../Redux/actions";
import { createDxpClickHandler } from "../../../Helpers/utils";
import "../../App/style.css";
import "../../css/registration.css";
import {
  DxpClickHandlerContext,
  DxpStandalonePageContext,
} from "./DxpStandaloneContexts";
import { buildDxpStaticPageContext } from "./dxpStaticPageContext";

type Props = {
  children: React.ReactNode;
  pageContextOverrides?: Record<string, unknown>;
  customHandlers?: Record<string, (props: unknown) => void>;
  requireCommonHeaders?: boolean;
  appType?: string;
  /** Appended to the inner `.dxp-template-root` element. */
  containerClassName?: string;
};

/**
 * Wraps DXP static pages with the same page context + click handler as the former JSON-driven templates.
 */
export default function DxpPageShell({
  children,
  pageContextOverrides = {},
  customHandlers,
  requireCommonHeaders = true,
  appType,
  containerClassName,
}: Props) {
  const dispatch = useDispatch();
  const registration = useSelector(
    (state: { registration?: { commonHeaders?: object } }) =>
      state.registration,
  );
  const commonHeaders = registration?.commonHeaders ?? {};

  useEffect(() => {
    if (Object.keys(commonHeaders).length === 0) {
      dispatch(loadCommonHeaders("dxp", appType));
    }
  }, [commonHeaders, dispatch, appType]);

  const pageContext = useMemo(
    () => buildDxpStaticPageContext(pageContextOverrides),
    [pageContextOverrides],
  );
  const clickHandler = useMemo(
    () => createDxpClickHandler(customHandlers),
    [customHandlers],
  );

  const ready = !requireCommonHeaders || Object.keys(commonHeaders).length > 0;

  if (!ready) {
    return <div className="fullScreen"> </div>;
  }

  return (
    <div className="registration-page-wrapper dxp-registration-page-shell">
      <DxpStandalonePageContext.Provider value={pageContext}>
        <DxpClickHandlerContext.Provider value={clickHandler}>
          <div
            className={["dxp-template-root", containerClassName]
              .filter(Boolean)
              .join(" ")}
          >
            {children}
          </div>
        </DxpClickHandlerContext.Provider>
      </DxpStandalonePageContext.Provider>
    </div>
  );
}
